import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'; // React와 훅들 임포트(상태/참조/메모/콜백/효과)
import '../styles/components/CertiUploader.css';

function CertiUploader({ files, setFiles, loading, max = 10, maxSizeMB = 10, label }) { // 부모에서 파일 배열/업데이트 함수/로딩상태/제한값을 전달받음
  const inputRef = useRef(null); // 숨겨진 <input type="file">를 클릭하기 위한 참조
  const [error, setError] = useState(''); // 마지막 검증 에러 메시지
  const accept = 'image/png,image/jpeg,image/jpg,image/webp,application/pdf'; // 허용할 이미지 MIME 목록
  const maxBytes = maxSizeMB * 1024 * 1024; // MB → 바이트로 변환

  // 미리보기 URL 생성(파일이 바뀔 때마다) ----------------------------------------------------
  const previews = useMemo( // 불필요한 재계산을 막기 위해 useMemo 사용
    () => files.map(f => ({ url: URL.createObjectURL(f), name: f.name, size: f.size })), // 각 파일에서 브라우저 임시 URL 생성
    [files] // files가 바뀔 때만 재계산
  );
  useEffect(() => () => previews.forEach(p => URL.revokeObjectURL(p.url)), [previews]); // 컴포넌트 언마운트/변경 시 임시 URL 해제(메모리 누수 방지)

  const openPicker = () => { if (!loading) inputRef.current?.click(); }; // 버튼을 눌렀을 때 파일 선택창 열기(로딩 중이면 무시)



  // 2) validatePicked에서 새 File 생성 대신 원본 File 그대로 사용
  const validatePicked = useCallback((picked) => {
    const problems = [];
    const valid = [];
    const allow = accept.split(',');
    for (const f of picked) {
      if (!allow.includes(f.type)) { problems.push(`${f.name}: 지원하지 않는 형식`); continue; }
      if (f.size > maxBytes) { problems.push(`${f.name}: ${maxSizeMB}MB 초과`); continue; }
      valid.push(f); // ← 여기서 new File(...)로 감싸지 말고 그대로 추가
    }
    return { valid, problems };
  }, [accept, maxBytes, maxSizeMB]);

  // 2) 기존 + 신규 병합, 최대개수 제한, (이름+크기) 기준 중복 제거 -----------------------------
  const mergeAndDedup = useCallback((current, incoming) => {
    const merged = [...current, ...incoming].slice(0, max); // 합치고 최대 개수 초과분은 자름
    const seen = new Set(); // 이미 본 파일 키를 기록
    const out = []; // 최종 결과
    for (const f of merged) {
      const key = `${f.name}_${f.size}`; // 간단한 중복 기준(이름+크기)
      if (!seen.has(key)) { seen.add(key); out.push(f); } // 중복이 아니면 추가
    }
    return out; // 병합/중복제거 결과
  }, [max]);

  // 3) 추가 처리: 검증 → 병합 → 상태/에러 반영 ------------------------------------------------
  const handleAdd = useCallback((incoming) => {
    const { valid, problems } = validatePicked(incoming); // 파일 검증
    const next = mergeAndDedup(files, valid); // 기존 목록과 병합 + 중복제거
    setFiles(next); // 부모 상태 업데이트(업로더 목록 반영)
    setError(problems.join(' / ')); // 문제가 있다면 한 줄로 표시
  }, [validatePicked, mergeAndDedup, files, setFiles]);

  // 파일 선택(input onChange) ----------------------------------------------------------------
  const onPick = (e) => {
    const picked = Array.from(e.target.files || []); // 선택된 FileList → 배열
    if (!picked.length) return; // 없으면 종료
    handleAdd(picked); // 검증/병합 수행
    e.target.value = ''; // 같은 파일을 다시 선택 가능하도록 초기화
  };

  // 드래그앤드롭 처리 ------------------------------------------------------------------------
  const onDrop = useCallback((e) => {
    e.preventDefault(); // 브라우저 기본 동작 방지
    if (loading) return; // 로딩 중이면 무시
    const dropped = Array.from(e.dataTransfer.files || []); // 드롭된 파일 목록
    if (!dropped.length) return; // 없으면 종료
    handleAdd(dropped); // 검증/병합 수행
  }, [loading, handleAdd]);
  const onDragOver = (e) => e.preventDefault(); // 드롭 가능 영역 유지를 위해 기본 동작 방지

  // 삭제/초기화 버튼 ----------------------------------------------------------------------------
  const removeAt = useCallback((idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx)); // 해당 인덱스의 파일 삭제
  }, [setFiles]);
  const clearAll = useCallback(() => setFiles([]), [setFiles]); // 전체 삭제

  return (
    <div className="certi-uploader">
      <p className="certi-uploader__label">{label}</p> {/* 안내 문구 */}

      <input
        ref={inputRef} // 숨겨진 파일 입력창을 참조
        type="file" // 파일 선택 입력
        multiple // 여러 개 선택 허용
        accept={accept} // 허용 형식만 선택 가능
        style={{ display: 'none' }} // 화면에는 보이지 않게 숨김
        onChange={onPick} // 파일 선택 시 처리
        disabled={loading} // 로딩 중 비활성화
      />

      <div
        onDrop={onDrop} // 드롭 시 파일 추가
        onDragOver={onDragOver} // 드래그 중 기본 동작 방지
        className="certi-uploader__dropzone"
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <span className="certi-uploader__dropzone-text">여기로 파일을 드래그하거나 버튼으로 선택하세요.</span> {/* 사용 안내 */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={openPicker} disabled={loading} className="certi-uploader__button">첨부(추가)</button> {/* 파일 선택 버튼 */}
          {files.length > 0 && (
            <button type="button" onClick={clearAll} disabled={loading} className="certi-uploader__clear-all">전체 삭제</button> // 모든 파일 제거
          )}
        </div>
      </div>

      {error && <div className="certi-uploader__error">{error}</div>} {/* 검증 에러 표시 */}

      <div className="certi-uploader__file-list" style={{ display: 'flex', gap: 16, alignItems: 'center', overflowX: 'auto' }}>
        {previews.map((p, idx) => ( // 미리보기 목록 렌더링(간단히 파일명만 표시)
          <div key={p.url} className="certi-uploader__file-item">
            <div className="certi-uploader__file-info">
              <div className="certi-uploader__file-name">{p.name}</div> {/* 파일명 */}
              <div className="certi-uploader__file-size">{(p.size / 1024).toFixed(1)} KB</div>
            </div>
            <button type="button" onClick={() => removeAt(idx)} aria-label="remove" className="certi-uploader__file-remove">X</button> {/* 개별 삭제 */}
          </div>
        ))}
      </div>

      <div className="certi-uploader__dropzone-hint">
        최대 {max}개, {maxSizeMB}MB 이하, 형식: PNG/JPG/WEBP/PDF {/* 업로드 제한 안내 */}
      </div>
    </div>
  );
}

export default CertiUploader; // 다른 컴포넌트에서 사용할 수 있도록 내보냄